import {
  type KeyboardEventHandler,
  type RefCallback,
  useCallback,
  useEffect,
  useId,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import NorthEastRounded from '@mui/icons-material/NorthEastRounded';
import SouthWestRounded from '@mui/icons-material/SouthWestRounded';
import { Box, ButtonBase, IconButton, Tooltip, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';

import { JotCharacter } from '../JotCharacter';

import { LAUNCHER_ACTION_GAP, LAUNCHER_ACTION_SIZE, LAUNCHER_SIZE } from './launcherPlacement';
import type { JotLauncherPlacementController } from './useJotLauncherPlacement';

export interface JotLauncherProps {
  placement: JotLauncherPlacementController;
  headerContainer: HTMLElement | null;
  onActivate: () => void;
  onDownloadDesktop?: () => void;
  open: boolean;
  panelId: string;
  buttonRef: RefCallback<HTMLButtonElement>;
  reducedMotion?: boolean;
  mood?: 'idle' | 'listening' | 'sending' | 'sent';
  reaction?: number;
  onKeyDown?: KeyboardEventHandler<HTMLButtonElement>;
}

export function JotLauncher({
  placement,
  headerContainer,
  onActivate,
  onDownloadDesktop,
  open,
  panelId,
  buttonRef,
  reducedMotion = false,
  mood = 'idle',
  reaction = 0,
  onKeyDown,
}: JotLauncherProps) {
  const movementHintId = useId();
  const button = useRef<HTMLButtonElement | null>(null);
  const restoreKeyboardFocus = useRef(false);
  const setButton = useCallback(
    (element: HTMLButtonElement | null) => {
      button.current = element;
      buttonRef(element);
    },
    [buttonRef],
  );
  const { docked } = placement;
  useEffect(() => {
    if (restoreKeyboardFocus.current) button.current?.focus({ preventScroll: true });
    restoreKeyboardFocus.current = false;
  }, [docked]);
  const launcher = (
    <Box
      data-jot-placement={docked ? 'header' : 'floating'}
      sx={{
        position: docked ? 'relative' : 'fixed',
        display: 'inline-flex',
        ...(!docked && {
          left: placement.position.x,
          top: placement.position.y,
          width: LAUNCHER_SIZE,
          height: LAUNCHER_SIZE,
        }),
        zIndex: theme => theme.zIndex.modal - 2,
        '& .jot-placement-actions': { opacity: 0, pointerEvents: 'none' },
        '&:hover .jot-placement-actions, &:has(:focus-visible) .jot-placement-actions': {
          opacity: 1,
          pointerEvents: 'auto',
        },
        ...(placement.dragging && {
          '& .jot-placement-actions': { opacity: 1, pointerEvents: 'auto' },
        }),
      }}
    >
      <ButtonBase
        ref={setButton}
        data-jot-launcher
        aria-label="Contact support"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-describedby={docked ? undefined : movementHintId}
        disableRipple
        {...placement.dragHandlers}
        onClick={event => {
          if (placement.consumeDragClick(event)) {
            event.preventDefault();
            event.stopPropagation();
            return;
          }
          onActivate();
        }}
        onKeyDown={event => {
          placement.onMoveKeyDown(event);
          onKeyDown?.(event);
        }}
        sx={{
          width: docked ? 'auto' : LAUNCHER_SIZE,
          height: docked ? 36 : LAUNCHER_SIZE,
          px: docked ? { xs: 0, sm: 1 } : 0,
          gap: 0.5,
          borderRadius: 1.5,
          color: 'primary.main',
          cursor: docked ? 'pointer' : placement.dragging ? 'grabbing' : 'grab',
          touchAction: docked ? 'auto' : 'none',
          '&.Mui-focusVisible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: 2,
          },
        }}
      >
        <Box
          sx={{
            width: docked ? 24 : LAUNCHER_SIZE,
            height: docked ? 24 : LAUNCHER_SIZE,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <JotCharacter
            mood={mood}
            reaction={reaction}
            size={docked ? 44 : 92}
            height={docked ? 46 : 100}
            reducedMotion={docked || reducedMotion}
          />
        </Box>
        {docked && (
          <Typography
            variant="body2"
            sx={{ display: { xs: 'none', sm: 'block' }, fontSize: 13, fontWeight: 500 }}
          >
            Jot
          </Typography>
        )}
      </ButtonBase>
      {(docked || headerContainer || onDownloadDesktop) && (
        <Box
          className="jot-placement-actions"
          sx={{
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            '& .MuiIconButton-root': {
              width: LAUNCHER_ACTION_SIZE,
              height: LAUNCHER_ACTION_SIZE,
              p: 0,
              color: 'text.secondary',
              '& .MuiSvgIcon-root': { fontSize: 14 },
              '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
            },
            ...(docked
              ? { right: '100%', top: '50%', transform: 'translateY(-50%)' }
              : {
                  top: `calc(100% + ${LAUNCHER_ACTION_GAP}px)`,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  bgcolor: 'background.paper',
                  borderRadius: 999,
                  boxShadow: 2,
                }),
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: '100%',
              left: 0,
              height: Math.max(0, LAUNCHER_ACTION_GAP),
              width: '100%',
            },
          }}
        >
          {onDownloadDesktop && (
            <Tooltip title="Take Jot with you">
              <IconButton aria-label="Take Jot with you" size="small" onClick={onDownloadDesktop}>
                <DownloadRounded fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {(docked || headerContainer) && (
            <Tooltip title={docked ? 'Undock Jot' : 'Dock in header'}>
              <IconButton
                aria-label={docked ? 'Undock Jot' : 'Dock Jot in header'}
                size="small"
                onClick={event => {
                  restoreKeyboardFocus.current = event.detail === 0;
                  placement.setDocked(!docked, event.detail === 0 ? 'keyboard' : 'pointer');
                }}
              >
                {docked ? (
                  <SouthWestRounded fontSize="small" />
                ) : (
                  <NorthEastRounded fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  );
  return (
    <>
      {docked ? headerContainer && createPortal(launcher, headerContainer) : launcher}
      {!docked && (
        <Box id={movementHintId} sx={visuallyHidden}>
          Drag to move, or use arrow keys while focused. Hold Shift for larger steps.
        </Box>
      )}
      <Box role="status" aria-live="polite" sx={visuallyHidden}>
        {placement.announcement}
      </Box>
    </>
  );
}
